import React, {useCallback, useState} from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";

import useMutation from "../../../hooks/useMutation";
import {CREATE_KIT_ASSET_MUTATION} from "../../../constants/graphql/mutations";
import useQuery from "../../../hooks/useQuery";
import {useKitAssets} from "../../../contexts/kit-assets";
import useNotification from "../../../hooks/notification";
import QuantitySelector from "./QuantitySelector";

export default function AddToKit({ asset, defaultValues, size = "medium" }) {
  const { notifySuccess } = useNotification();
  const [createKitAsset, { loading }] = useMutation(CREATE_KIT_ASSET_MUTATION);
  const [error, setError] = useState(null);
  const notAvailable = false;
  const [quantity, setQuantity] = useState(notAvailable ? 0 : 1);
  const query = useQuery();
  const { refetch } = useKitAssets();

  const handleAddInventoryItem = () => {
    if (quantity === 0) return;

    return createKitAsset({
      variables: { kitId: query.get("kitId"), input: { quantity, assetId: asset.id } },
      onSuccess: ({ kit, asset }) => {
        setError(null);
        setQuantity(1);
        refetch();
        notifySuccess("Component successfully added");
      },
      onFailure: errors => {
        setError(errors.quantity || errors.asset || errors.submit);
      },
    });
  };

  const updateQuantity = useCallback(
    value => {
      if (value >= 0) {
        setQuantity(value);
      }
    },
    [asset.availableQuantity, setQuantity]
  );

  const handleMinus = useCallback(() => updateQuantity(quantity - 1), [quantity, updateQuantity]);

  const handlePlus = useCallback(() => updateQuantity(quantity + 1), [quantity, updateQuantity]);

  const handleUpdate = useCallback(
    event => updateQuantity(event.target.value ? parseInt(event.target.value, 10) : 0),
    [quantity, updateQuantity]
  );

  return (
    <>
      <Box display="flex" justifyContent="space-between" width="100%">
        <QuantitySelector
          quantity={quantity}
          onMinus={handleMinus}
          onPlus={handlePlus}
          onChange={handleUpdate}
          disabled={notAvailable || loading}
        />
        <Button
          size="small"
          variant="outlined"
          disabled={notAvailable}
          onClick={handleAddInventoryItem}
          style={{
            minWidth: 100,
            borderRadius: "25px",
            height: "36px",
          }}
        >
          Add to Kit
        </Button>
      </Box>
      {error && (
        <Box mt={1}>
          <FormHelperText htmlFor="barcode" error={!!error}>
            {error}
          </FormHelperText>
        </Box>
      )}
    </>
  );
}
